import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import {
    formatCurrencyValue,
    getFullPaymentAmount,
    getPartialPaymentAmount
} from 'ts/common/components/invoice/helpers';
import { useTranslate } from 'ts/common/hooks';
import { type ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import ClientPaymentSummary from 'client_react/common/components/ClientPaymentSummary';
import { FormFields } from 'client_react/invoice/components/StripeInvoicePaymentForm';

interface Props {
    paymentAmountType: ClientPaymentOption;
}

const InvoicePaymentSummary = ({ paymentAmountType }: Props) => {
    const { invoice } = useOutletContext<{ invoice: SpApi.Client.IInvoice }>();
    const { watch } = useFormContext<FormFields>();
    const t = useTranslate('clientInvoice');

    const tipAmount = watch('tipAmount');

    const totalPaymentAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(invoice)
            : getPartialPaymentAmount(invoice);
    const totalPrice = formatCurrencyValue(
        totalPaymentAmount + (tipAmount ?? 0),
        invoice.currencyCode
    );

    const discountAmount = Number(invoice.discountTotal);
    const salesTaxAmount = Number(invoice.salesTaxTotal);

    return (
        <ClientPaymentSummary
            showBreakdown={
                invoice.isTippingEnabled ||
                ((Number(invoice.salesTaxTotal) > 0 || Number(invoice.discountTotal) > 0) &&
                    paymentAmountType === 'full' &&
                    invoice.dueTotal === invoice.grandTotal)
            }
            discounts={
                discountAmount
                    ? formatCurrencyValue(discountAmount, invoice.currencyCode)
                    : undefined
            }
            discountsSubtitle={t('discounts')}
            salesTax={
                salesTaxAmount
                    ? formatCurrencyValue(salesTaxAmount, invoice.currencyCode)
                    : undefined
            }
            salesTaxSubtitle={t('paymentSummaryTax')}
            subtotal={formatCurrencyValue(Number(invoice.subtotal), invoice.currencyCode)}
            subtotalSubtitle={t('paymentSummarySubtotal')}
            tipAmount={formatCurrencyValue(tipAmount ?? 0, invoice.currencyCode)}
            tipAmountSubtitle={t('tip')}
            title={t('paymentSummary')}
            totalPrice={totalPrice}
            totalSubtitle={t('paymentSummaryTotal')}
        />
    );
};

export default InvoicePaymentSummary;
